import { EDataUnit } from "@/constants/data-units.type";
import { TSiteAddress } from "@/data/api/kili/sites/types/sites.type";
import { EHeaterProgramMetaMode, TProgramMetaMode } from "@/domains/v4b/program/subprogram.types";
import { useTypedIntl } from "@/locales/typed-intl";
import { ECountryCode } from "@/utils/country";
import { makeGuard } from "@/utils/object";

export function translateBoolToYesOrNo(boolValue: boolean) {
  const { t, k } = useTypedIntl();

  const translationKey = boolValue ? "yes" : "no";
  return t(k.global.misc[translationKey]);
}

export function getEnumItemTranslationKey(
  e: Record<string, string>,
  enumKey: string,
  item?: string | null,
  fallbackKey: string = "global.misc.other",
): string {
  if (!item) {
    return "-";
  }

  if (Object.values(e).includes(item)) {
    return `${enumKey}.${item}`;
  }

  return fallbackKey;
}

export enum EPrefixSymbol {
  NONE = "none",
  KILO = "k",
  MEGA = "M",
  GIGA = "G",
  TERA = "T",
}

export const UNIT_PREFIXES: Record<EPrefixSymbol, number> = Object.freeze({
  [EPrefixSymbol.NONE]: 1,
  [EPrefixSymbol.KILO]: 1e3,
  [EPrefixSymbol.MEGA]: 1e6,
  [EPrefixSymbol.GIGA]: 1e9,
  [EPrefixSymbol.TERA]: 1e12,
});

const arrayUnitPrefixes = Object.values(EPrefixSymbol).map((symbol) => {
  return { value: UNIT_PREFIXES[symbol], symbol: symbol };
});

function getValuePrefix(value?: number | null) {
  if (typeof value !== "number") {
    return;
  }

  return arrayUnitPrefixes.findLast((prefix) => value >= prefix.value);
}

export function getMaybeValuePrefixSymbol(value?: number | null) {
  const maybePrefix = getValuePrefix(value);

  return maybePrefix?.symbol === EPrefixSymbol.NONE ? undefined : maybePrefix?.symbol;
}

export function formatWhValue(value?: number | null, decimals: number = 1): string {
  const { t, k, n } = useTypedIntl();

  if (typeof value !== "number") {
    return t(k.global.units[EDataUnit.WATT_HOUR]({ value: "—", prefix: "" }));
  }

  const prefix = getValuePrefix(value);

  if (!prefix || prefix.symbol === EPrefixSymbol.NONE) {
    return t(k.global.units[EDataUnit.WATT_HOUR]({ value, prefix: "" }));
  }

  return t(
    k.global.units[EDataUnit.WATT_HOUR]({
      value: n(value / prefix.value, { maximumFractionDigits: decimals }),
      prefix: prefix.symbol,
    }),
  );
}

export function getValueByPrefix(initialValue: number, prefix?: EPrefixSymbol, decimals: number = 1): number {
  return (
    Math.trunc((prefix ? initialValue / UNIT_PREFIXES[prefix] : initialValue) * Math.pow(10, decimals)) /
    Math.pow(10, decimals)
  );
}

const detailedModes = [
  EHeaterProgramMetaMode.COMFORT,
  EHeaterProgramMetaMode.ECO,
  EHeaterProgramMetaMode.NO_FROST,
] as const satisfies readonly TProgramMetaMode[];

const isDetailedMode = makeGuard(detailedModes);

export function getModeEquivalentForNotFR(metaMode: TProgramMetaMode, siteAddress?: TSiteAddress) {
  const { t, k } = useTypedIntl();
  const siteCountryCode = siteAddress?.countryIsoCode;

  if (!siteAddress || !isDetailedMode(metaMode)) return;

  if (siteCountryCode === ECountryCode.FRANCE) return;

  return t(k.global.program.metaMode.equivalent[metaMode]);
}
